import { useState, useEffect, useRef, useCallback } from "react";
import {
  TrendingUp,
  Activity,
  Layers,
  ShoppingCart,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BookOpen,
  AlertTriangle,
  CheckCircle,
  MinusCircle,
  RefreshCw,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Clock,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label,
} from "recharts";
import { API, CHART } from "../../lib/constants";
import { IndustryBreakdown } from "../industry/IndustryBreakdown";

const AXIS_TICK = CHART.axis;
const AXIS_STROKE = CHART.axisStroke;
const GRID_STROKE = CHART.gridStroke;
const TOOLTIP_STYLE = CHART.tooltip;

const ORIENT_COLORS = {
  utility: "#3b82f6",
  creative: "#8b5cf6",
  care: "#10b981",
  extraction: "#ef4444",
};

const ORIENT_NAMES = {
  utility: "Utility",
  creative: "Creative",
  care: "Care",
  extraction: "Extraction",
};

const MONET_COLORS = {
  free: "#22c55e",
  freemium: "#10b981",
  subscription: "#f59e0b",
  enterprise: "#8b5cf6",
  usage_based: "#3b82f6",
  commission: "#ec4899",
  ads: "#ef4444",
};

const MONET_NAMES = {
  free: "Free",
  freemium: "Freemium",
  subscription: "Subscription",
  enterprise: "Enterprise",
  usage_based: "Usage-Based",
  commission: "Commission",
  ads: "Ads",
};

const GEROSKI_PHASES = ["Introduction", "Growth", "Maturity", "Saturation"];
const GEROSKI_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

const AI_MILESTONES = [
  { month: "2022-11", label: "ChatGPT Launch", color: "#f59e0b" },
  { month: "2023-02", label: "Bing AI / Sydney", color: "#8b5cf6" },
  { month: "2023-03", label: "GPT-4 Release", color: "#10b981" },
  { month: "2023-07", label: "Llama 2 Open Source", color: "#3b82f6" },
  { month: "2023-12", label: "Gemini Launch", color: "#ec4899" },
  { month: "2024-02", label: "Gemini Ultra / Sora", color: "#ef4444" },
  { month: "2024-05", label: "GPT-4o Launch", color: "#10b981" },
  { month: "2024-11", label: "ChatGPT turns 2", color: "#f59e0b" },
  { month: "2025-01", label: "DeepSeek R1", color: "#3b82f6" },
];

const fmtMonth = (v) => {
  if (!v) return "";
  const [y, m] = v.split("-");
  const names = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(m)] || m} '${y.slice(2)}`;
};

export const IndustryTrends = () => {
  const [data, setData] = useState(null);
  const [execSummary, setExecSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/industry-trends`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadSummary = () => {
    setSummaryLoading(true);
    fetch(`${API}/industry-trends/executive-summary`)
      .then((r) => r.json())
      .then((d) => setExecSummary(d.summary))
      .catch(() => {})
      .finally(() => setSummaryLoading(false));
  };

  // Auto-load summary when data is ready
  useEffect(() => {
    if (data && !execSummary && !summaryLoading) loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="trends-loading">
        <div className="text-center">
          <Activity className="w-8 h-8 animate-pulse text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading industry trends...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="industry-trends-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-primary" />
          Industry Trends
        </h2>
        <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
          Adoption curves, hype cycles, market shifts, and consumption patterns derived from {data.total_apps.toLocaleString()} AI applications
          ({data.time_range.start} to {data.time_range.end}).
        </p>
      </div>

      {/* Executive Summary (LLM) */}
      <ExecutiveSummary summary={execSummary} loading={summaryLoading} onReload={loadSummary} />

      {/* Industry Clusters with AICCI */}
      <IndustryBreakdown entityType="application" />

      {/* Key Metrics Banner */}
      <KeyMetricsBanner data={data} />

      {/* 1. Geroski S-Curves */}
      <GeroskiSCurves data={data} />

      {/* 2. Gartner Hype Cycle */}
      <GartnerHypeCycle data={data} />

      {/* 3. Market Shift */}
      <MarketShift data={data} />

      {/* 4. Consumption Patterns */}
      <ConsumptionPatterns data={data} />

      {/* 5. Predictions & Outlook */}
      <PredictionsOutlook data={data} />
    </div>
  );
};

/* ────── Executive Summary (LLM-powered) ────── */
const ExecutiveSummary = ({ summary, loading, onReload }) => {
  const outlookIcons = {
    positive: <CheckCircle className="w-4 h-4 text-emerald-400" />,
    caution: <AlertTriangle className="w-4 h-4 text-amber-400" />,
    neutral: <MinusCircle className="w-4 h-4 text-blue-400" />,
  };
  const outlookBg = {
    positive: "border-emerald-500/20 bg-emerald-500/5",
    caution: "border-amber-500/20 bg-amber-500/5",
    neutral: "border-blue-500/20 bg-blue-500/5",
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="executive-summary">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary" />
          Executive Briefing
          <span className="text-[10px] font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">AI-Generated</span>
        </h3>
        <button
          onClick={onReload}
          disabled={loading}
          className="p-1.5 hover:bg-muted rounded-lg transition-colors"
          data-testid="reload-summary-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""} text-muted-foreground`} />
        </button>
      </div>

      {loading && !summary && (
        <div className="flex items-center gap-3 py-8 justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading industry briefing...</span>
        </div>
      )}

      {summary && (
        <div className="space-y-5">
          {/* Headline */}
          <div>
            <h4 className="text-lg font-bold text-foreground leading-snug" data-testid="exec-headline">
              {summary.headline}
            </h4>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {summary.executive_summary}
            </p>
          </div>

          {/* Key Signals */}
          {summary.key_signals?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {summary.key_signals.map((sig, i) => (
                <div key={sig.signal || i} className={`rounded-lg border p-3 ${outlookBg[sig.outlook] || outlookBg.neutral}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {outlookIcons[sig.outlook] || outlookIcons.neutral}
                    <span className="text-xs font-semibold">{sig.signal}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{sig.assessment}</p>
                </div>
              ))}
            </div>
          )}

          {/* Industry Context */}
          {summary.industry_context?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Foundational Framework References</h5>
              {summary.industry_context.map((ctx, i) => (
                <div key={ctx.source || i} className="flex items-start gap-3 px-3 py-2 rounded-lg bg-muted/20">
                  <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">
                    {ctx.source}
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{ctx.insight}</p>
                </div>
              ))}
            </div>
          )}

          {/* Bottom Line */}
          {summary.bottom_line && (
            <div className="px-4 py-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm font-medium text-foreground">
                <span className="text-primary font-semibold">Bottom Line: </span>
                {summary.bottom_line}
              </p>
            </div>
          )}
        </div>
      )}

      {!loading && !summary && (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">Failed to generate summary.</p>
          <button onClick={onReload} className="text-xs text-primary mt-2 hover:underline">Retry</button>
        </div>
      )}
    </div>
  );
};

/* ────── Key Metrics Banner ────── */
const KeyMetricsBanner = ({ data }) => {
  const gr = data.growth_rates;
  const latestGrowth = gr.length > 0 ? gr[gr.length - 1] : null;
  const peakYear = gr.reduce((best, r) => (r.yoy_growth_pct > (best?.yoy_growth_pct || 0) ? r : best), null);
  const partial = data.partial_year;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="trends-metrics-banner">
      <MetricCard
        label="Dataset Span"
        value={`${data.time_range.start.slice(0, 4)}-${data.time_range.end.slice(0, 4)}`}
        sub={`${data.s_curves.length} months of data`}
        icon={Activity}
        color="text-blue-400"
      />
      <MetricCard
        label="Peak Growth Year"
        value={peakYear ? peakYear.year : "—"}
        sub={peakYear ? `+${peakYear.yoy_growth_pct}% YoY` : ""}
        icon={Zap}
        color="text-amber-400"
      />
      <MetricCard
        label="Latest Full-Year Growth"
        value={latestGrowth ? `${latestGrowth.yoy_growth_pct > 0 ? "+" : ""}${latestGrowth.yoy_growth_pct}%` : "—"}
        sub={latestGrowth ? `${latestGrowth.year} vs ${parseInt(latestGrowth.year) - 1}` : ""}
        icon={latestGrowth?.yoy_growth_pct > 0 ? ArrowUpRight : ArrowDownRight}
        color={latestGrowth?.yoy_growth_pct > 20 ? "text-emerald-400" : latestGrowth?.yoy_growth_pct > 0 ? "text-blue-400" : "text-red-400"}
      />
      {partial ? (
        <MetricCard
          label={`${partial.year} (Partial)`}
          value={`${partial.count.toLocaleString()} apps`}
          sub={`${partial.months_elapsed}mo elapsed, ~${partial.annualized_estimate.toLocaleString()} annualized`}
          icon={TrendingUp}
          color="text-violet-400"
        />
      ) : (
        <MetricCard
          label="Fastest Growing"
          value={(() => {
            const m = data.momentum;
            const best = Object.entries(m).reduce((a, [k, v]) => (v.change_pct > (a[1]?.change_pct || -Infinity) ? [k, v] : a), ["", null]);
            return ORIENT_NAMES[best[0]] || "—";
          })()}
          sub={(() => {
            const m = data.momentum;
            const best = Object.entries(m).reduce((a, [k, v]) => (v.change_pct > (a[1]?.change_pct || -Infinity) ? [k, v] : a), ["", null]);
            return best[1] ? `+${best[1].change_pct}% recent momentum` : "";
          })()}
          icon={TrendingUp}
          color="text-violet-400"
        />
      )}
    </div>
  );
};

const MetricCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-xl font-bold">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

/* ────── 1. Geroski S-Curves ────── */
const GeroskiSCurves = ({ data }) => {
  const phases = data.geroski_phases;
  const allMonths = data.s_curves.map((d) => d.month);
  const totalMonths = allMonths.length;

  // Time Machine state
  const [monthIdx, setMonthIdx] = useState(totalMonths - 1);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(150); // ms per frame
  const intervalRef = useRef(null);

  // Auto-play logic
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setMonthIdx((prev) => {
          if (prev >= totalMonths - 1) {
            setPlaying(false);
            return totalMonths - 1;
          }
          return prev + 1;
        });
      }, speed);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, speed, totalMonths]);

  const togglePlay = useCallback(() => {
    if (monthIdx >= totalMonths - 1) setMonthIdx(0); // restart if at end
    setPlaying((p) => !p);
  }, [monthIdx, totalMonths]);

  const slicedData = data.s_curves.slice(0, monthIdx + 1);
  const currentMonth = allMonths[monthIdx] || "";

  // Find milestones that have occurred up to current month
  const activeMilestones = AI_MILESTONES.filter((m) => m.month <= currentMonth);
  // Current milestone (most recent)
  const currentMilestone = activeMilestones.length > 0 ? activeMilestones[activeMilestones.length - 1] : null;

  // Compute live stats at current time point
  const currentPoint = slicedData[slicedData.length - 1] || {};
  const totalAtPoint = data.orientations.reduce((s, o) => s + (currentPoint[o] || 0), 0);

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="s-curves-chart">
      <div className="mb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            Technology Diffusion S-Curves
            <span className="text-[10px] font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Geroski Framework</span>
          </h3>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] font-medium text-amber-400 uppercase tracking-wide">Time Machine</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Cumulative adoption curves following Geroski's diffusion model. Press play to watch the AI boom unfold month-by-month.
        </p>
      </div>

      {/* ── Time Machine Controls ── */}
      <div className="mt-4 mb-2 bg-muted/20 rounded-xl border border-border p-4" data-testid="time-machine-controls">
        {/* Current time display */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="w-9 h-9 rounded-full bg-primary/20 hover:bg-primary/30 flex items-center justify-center transition-colors"
              data-testid="time-machine-play-btn"
            >
              {playing ? (
                <Pause className="w-4 h-4 text-primary" />
              ) : (
                <Play className="w-4 h-4 text-primary ml-0.5" />
              )}
            </button>
            <div>
              <div className="text-lg font-bold tabular-nums" data-testid="time-machine-date">
                {fmtMonth(currentMonth)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {totalAtPoint.toLocaleString()} apps tracked
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setPlaying(false); setMonthIdx(0); }}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
              title="Reset"
            >
              <SkipBack className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
            <button
              onClick={() => { setPlaying(false); setMonthIdx(totalMonths - 1); }}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
              title="Skip to end"
            >
              <SkipForward className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
            <select
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="text-[10px] bg-muted border border-border rounded px-1.5 py-1 text-muted-foreground"
              data-testid="time-machine-speed"
            >
              <option value={300}>0.5x</option>
              <option value={150}>1x</option>
              <option value={75}>2x</option>
              <option value={40}>4x</option>
            </select>
          </div>
        </div>

        {/* Timeline slider with milestones */}
        <div className="relative">
          <input
            type="range"
            min={0}
            max={totalMonths - 1}
            value={monthIdx}
            onChange={(e) => { setPlaying(false); setMonthIdx(Number(e.target.value)); }}
            className="w-full h-1.5 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
            data-testid="time-machine-slider"
          />
          {/* Milestone markers on the slider */}
          <div className="relative h-6 mt-1">
            {AI_MILESTONES.map((m) => {
              const idx = allMonths.indexOf(m.month);
              if (idx < 0) return null;
              const pct = (idx / (totalMonths - 1)) * 100;
              const isActive = m.month <= currentMonth;
              return (
                <div
                  key={m.month}
                  className="absolute -translate-x-1/2 flex flex-col items-center"
                  style={{ left: `${pct}%` }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: isActive ? m.color : "hsl(240, 5%, 30%)" }}
                  />
                  <span
                    className="text-[7px] whitespace-nowrap mt-0.5 max-w-[60px] truncate"
                    style={{ color: isActive ? m.color : "hsl(240, 5%, 35%)" }}
                    title={m.label}
                  >
                    {m.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current milestone callout */}
        {currentMilestone && (
          <div
            className="mt-1 px-3 py-1.5 rounded-lg border text-center"
            style={{
              borderColor: `${currentMilestone.color}40`,
              backgroundColor: `${currentMilestone.color}10`,
            }}
          >
            <span className="text-xs font-semibold" style={{ color: currentMilestone.color }}>
              {currentMilestone.label}
            </span>
            <span className="text-[10px] text-muted-foreground ml-2">
              {fmtMonth(currentMilestone.month)}
            </span>
          </div>
        )}
      </div>

      {/* Lifecycle Phase Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 mb-4">
        {data.orientations.map((o) => {
          const p = phases[o];
          const phaseIdx = p.phase_idx;
          const countAtPoint = currentPoint[o] || 0;
          return (
            <div
              key={o}
              className="rounded-lg border p-3"
              style={{ borderColor: `${ORIENT_COLORS[o]}40`, backgroundColor: `${ORIENT_COLORS[o]}08` }}
              data-testid={`geroski-phase-${o}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ORIENT_COLORS[o] }} />
                  <span className="text-xs font-semibold">{ORIENT_NAMES[o]}</span>
                </div>
                <span className="text-xs font-bold tabular-nums" style={{ color: ORIENT_COLORS[o] }}>
                  {countAtPoint.toLocaleString()}
                </span>
              </div>
              <div className="text-sm font-bold" style={{ color: GEROSKI_COLORS[phaseIdx] }}>
                {p.phase}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed line-clamp-2">{p.evidence}</p>
              <div className="flex gap-0.5 mt-2">
                {GEROSKI_PHASES.map((phase, i) => (
                  <div
                    key={phase}
                    className="h-1 flex-1 rounded-full"
                    style={{
                      backgroundColor: i <= phaseIdx ? GEROSKI_COLORS[phaseIdx] : "hsl(240, 5%, 20%)",
                    }}
                  />
                ))}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[8px] text-muted-foreground">Intro</span>
                <span className="text-[8px] text-muted-foreground">Saturation</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* S-Curve Chart — animated with sliced data */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={slicedData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              dataKey="month"
              stroke={AXIS_STROKE}
              tick={AXIS_TICK}
              interval={11}
              tickFormatter={fmtMonth}
              domain={[allMonths[0], allMonths[allMonths.length - 1]]}
            />
            <YAxis
              stroke={AXIS_STROKE}
              tick={AXIS_TICK}
              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
              domain={[0, Math.max(...data.orientations.map((o) => data.s_curves[data.s_curves.length - 1]?.[o] || 0)) * 1.1]}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              labelFormatter={fmtMonth}
              formatter={(v, name) => [`${v.toLocaleString()} apps`, ORIENT_NAMES[name] || name]}
            />
            <Legend wrapperStyle={CHART.legend} formatter={(v) => `${ORIENT_NAMES[v] || v} (${phases[v]?.phase || ""})`} />
            {/* Milestone reference lines */}
            {activeMilestones.map((m) => (
              <ReferenceLine key={m.month} x={m.month} stroke={m.color} strokeDasharray="3 3" strokeOpacity={0.5}>
                <Label value={m.label} position="top" fill={m.color} fontSize={8} />
              </ReferenceLine>
            ))}
            {data.orientations.map((o) => (
              <Line
                key={o}
                type="monotone"
                dataKey={o}
                stroke={ORIENT_COLORS[o]}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Geroski insight */}
      <div className="mt-4 px-3 py-2.5 rounded-lg bg-blue-500/5 border border-blue-500/15">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-blue-400 font-semibold">Geroski's Diffusion Theory:</span>{" "}
          Technology adoption follows an S-shaped curve driven by network effects and information cascades.
          The steep acceleration post-ChatGPT (Nov 2022) reflects the "tipping point" predicted by Geroski, where early adopter success signals
          trigger rapid majority adoption. The current deceleration suggests market saturation in mature categories,
          while extraction AI remains in the growth phase, a pattern consistent with late-cycle speculative entry.
        </p>
      </div>

      <div className="mt-2 px-3 py-2 rounded-lg bg-muted/20 border border-border">
        <p className="text-[10px] text-muted-foreground">
          Data note: {data.total_apps.toLocaleString()} total apps in dataset. Time Machine shows the {totalMonths} months with dated launch data ({totalAtPoint.toLocaleString()} apps with known dates).
        </p>
      </div>
    </div>
  );
};

/* ────── 2. Gartner Hype Cycle ────── */
const GartnerHypeCycle = ({ data }) => {
  const phases = data.gartner_phases;

  const GARTNER_PHASE_COLORS = ["#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#10b981"];

  // Generate the idealized hype cycle curve (higher-res viewBox)
  const W = 900, H = 320, PAD_TOP = 30, PAD_BOT = 20, PAD_X = 40;
  const chartW = W - PAD_X * 2;
  const chartH = H - PAD_TOP - PAD_BOT;

  const hypeCurvePoints = [];
  for (let i = 0; i <= 200; i++) {
    const t = i / 200; // 0..1
    const x = PAD_X + t * chartW;
    let y;
    if (t <= 0.20) y = t / 0.20 * 0.50; // rise to 50%
    else if (t <= 0.33) y = 0.50 + (t - 0.20) / 0.13 * 0.50; // peak at 100%
    else if (t <= 0.50) y = 1.0 - (t - 0.33) / 0.17 * 0.85; // drop to trough 15%
    else if (t <= 0.55) y = 0.15 - (t - 0.50) / 0.05 * 0.05; // trough bottom 10%
    else if (t <= 0.80) y = 0.10 + (t - 0.55) / 0.25 * 0.55; // slope up to 65%
    else y = 0.65 + (t - 0.80) / 0.20 * 0.05; // plateau ~70%
    const yPx = PAD_TOP + chartH * (1 - Math.max(y, 0.02));
    hypeCurvePoints.push({ x, y: yPx });
  }
  const pathD = `M ${hypeCurvePoints.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L ")}`;

  // Phase label positions (x center, as fraction of chart width)
  const phasePositions = [
    { label: "Innovation\nTrigger", frac: 0.10 },
    { label: "Peak of Inflated\nExpectations", frac: 0.30 },
    { label: "Trough of\nDisillusionment", frac: 0.50 },
    { label: "Slope of\nEnlightenment", frac: 0.70 },
    { label: "Plateau of\nProductivity", frac: 0.90 },
  ];

  // Get position on the curve for a given x_position (0-100)
  const getPointOnCurve = (xPos) => {
    const idx = Math.min(Math.round((xPos / 100) * 200), 200);
    return hypeCurvePoints[idx] || hypeCurvePoints[0];
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="hype-cycle-chart">
      <div className="mb-1">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          AI Hype Cycle
          <span className="text-[10px] font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Inspired by Gartner</span>
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Inspired by Gartner's Hype Cycle framework. We map each AI orientation to the five canonical phases
          using our dataset's velocity patterns. This is our empirical interpretation, not an official Gartner placement.
        </p>
      </div>

      {/* Phase position visualization — crisp SVG */}
      <div className="mt-4 mb-4">
        <div className="relative bg-muted/10 rounded-lg overflow-visible border border-border" data-testid="gartner-visual">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            preserveAspectRatio="xMidYMid meet"
            overflow="visible"
          >
            {/* Background grid line */}
            <line x1={PAD_X} y1={PAD_TOP + chartH * 0.5} x2={W - PAD_X} y2={PAD_TOP + chartH * 0.5}
              stroke="hsl(240, 5%, 17%)" strokeDasharray="6 6" />

            {/* Hype curve path */}
            <path d={pathD} fill="none" stroke="hsl(240, 5%, 40%)" strokeWidth="2.5" strokeDasharray="8 4" />

            {/* Phase region dividers (subtle vertical lines) */}
            {[0.20, 0.42, 0.55, 0.80].map((frac) => (
              <line key={`divider-${frac}`}
                x1={PAD_X + frac * chartW} y1={PAD_TOP}
                x2={PAD_X + frac * chartW} y2={PAD_TOP + chartH + 5}
                stroke="hsl(240, 5%, 15%)" strokeDasharray="3 6" />
            ))}

            {/* Phase labels removed — rendered as HTML below */}

            {/* Y-axis label */}
            <text x={PAD_X - 5} y={PAD_TOP - 10} fill="hsl(240, 5%, 50%)" fontSize="11" fontFamily="system-ui, sans-serif">
              Expectations
            </text>
            {/* X-axis arrow */}
            <text x={W - PAD_X} y={H - 4} fill="hsl(240, 5%, 50%)" fontSize="11" textAnchor="end" fontFamily="system-ui, sans-serif">
              Time
            </text>
          </svg>

          {/* Category dots as HTML overlays (avoids React dev-tool span injection in SVG) */}
          {data.orientations.map((o) => {
            const p = phases[o];
            const pt = getPointOnCurve(p.x_position);
            const bucket = Math.round(p.x_position / 5) * 5;
            const sameGroup = data.orientations.filter(
              (x) => Math.round(phases[x].x_position / 5) * 5 === bucket
            );
            const idxInGroup = sameGroup.indexOf(o);
            const spread = 40;
            const yOff = sameGroup.length > 1
              ? (idxInGroup - (sameGroup.length - 1) / 2) * spread
              : 0;
            // Convert SVG coords to percentage positions
            const xPct = ((pt.x + yOff * 0.4) / W) * 100;
            const yPct = ((pt.y + yOff) / H) * 100;
            return (
              <div
                key={o}
                className="absolute flex flex-col items-center pointer-events-none"
                style={{
                  left: `${xPct}%`,
                  top: `${yPct}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                {/* Label pill */}
                <div
                  className="px-2 py-0.5 rounded-md text-xs font-bold mb-1 border whitespace-nowrap"
                  style={{
                    color: ORIENT_COLORS[o],
                    backgroundColor: "hsl(240, 6%, 12%)",
                    borderColor: `${ORIENT_COLORS[o]}80`,
                  }}
                >
                  {ORIENT_NAMES[o]}
                </div>
                {/* Dot */}
                <div
                  className="rounded-full border-2"
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: `${ORIENT_COLORS[o]}40`,
                    borderColor: ORIENT_COLORS[o],
                    boxShadow: `0 0 12px ${ORIENT_COLORS[o]}40`,
                  }}
                >
                  <div
                    className="rounded-full mx-auto mt-[3px]"
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: ORIENT_COLORS[o],
                    }}
                  />
                </div>
              </div>
            );
          })}
          {/* HTML phase labels below SVG for crisp rendering */}
          <div className="flex justify-between px-[4.5%] mt-1">
            {[
              "Innovation\nTrigger",
              "Peak of Inflated\nExpectations",
              "Trough of\nDisillusionment",
              "Slope of\nEnlightenment",
              "Plateau of\nProductivity",
            ].map((label) => (
              <div key={label.replace(/\n/g, '-')} className="text-center" style={{ width: "18%" }}>
                {label.split("\n").map((line) => (
                  <div key={line} className="text-[10px] text-muted-foreground leading-tight">{line}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phase detail cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {data.orientations.map((o) => {
          const p = phases[o];
          const phaseColor = GARTNER_PHASE_COLORS[p.phase_idx];
          return (
            <div key={o} className="rounded-lg border border-border p-3 bg-muted/10" data-testid={`gartner-phase-${o}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ORIENT_COLORS[o] }} />
                <span className="text-xs font-semibold">{ORIENT_NAMES[o]}</span>
              </div>
              <div className="text-sm font-bold" style={{ color: phaseColor }}>
                {p.phase}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
                <div>Peak: {p.peak_month ? fmtMonth(p.peak_month) : "—"} ({p.peak_velocity} apps/mo)</div>
                <div>Current: {p.current_vs_peak_pct}% of peak velocity</div>
                <div>{p.months_since_peak} months since peak</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Monthly Velocity Chart (supporting data) */}
      <div className="mt-5">
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Supporting Data: Monthly Launch Velocity</h4>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.hype_cycle}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis dataKey="month" stroke={AXIS_STROKE} tick={AXIS_TICK} interval={11} tickFormatter={fmtMonth} />
              <YAxis stroke={AXIS_STROKE} tick={AXIS_TICK} />
              <Tooltip
                {...TOOLTIP_STYLE}
                labelFormatter={fmtMonth}
                formatter={(v, name) => [`${v} apps`, ORIENT_NAMES[name] || name]}
              />
              <Legend wrapperStyle={CHART.legend} formatter={(v) => ORIENT_NAMES[v] || v} />
              {/* AI milestone markers */}
              {AI_MILESTONES.filter((m) => data.hype_cycle.some((d) => d.month === m.month)).map((m) => (
                <ReferenceLine key={m.month} x={m.month} stroke={m.color} strokeDasharray="3 3" strokeOpacity={0.6}>
                  <Label value={m.label} position="top" fill={m.color} fontSize={8} />
                </ReferenceLine>
              ))}
              {data.orientations.map((o) => (
                <Area
                  key={o}
                  type="monotone"
                  dataKey={o}
                  stroke={ORIENT_COLORS[o]}
                  fill={ORIENT_COLORS[o]}
                  fillOpacity={0.12}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Attribution & context */}
      <div className="mt-4 px-3 py-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-amber-400 font-semibold">Inspired by Gartner's Hype Cycle:</span>{" "}
          This visualization adapts Gartner's framework to our dataset of {data.total_apps.toLocaleString()} AI applications.
          Phase placements are empirically derived from launch velocity patterns (peak timing, post-peak decline, recovery trajectory). These are not official Gartner assessments.
          The five phases describe a recurring pattern: technologies attract inflated expectations before a trough of disillusionment, followed by productive maturity.
        </p>
      </div>
    </div>
  );
};

/* ────── 3. Market Shift ────── */
const MarketShift = ({ data }) => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="market-shift-chart">
    <div className="mb-1">
      <h3 className="text-base font-semibold flex items-center gap-2">
        <Layers className="w-4 h-4 text-primary" />
        Market Composition Shift
      </h3>
      <p className="text-xs text-muted-foreground mt-1">
        How the share of each AI orientation has evolved month-over-month. A rising wedge indicates growing market dominance.
      </p>
    </div>
    <div className="h-80 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data.market_shift} stackOffset="expand">
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis dataKey="month" stroke={AXIS_STROKE} tick={AXIS_TICK} interval={11} tickFormatter={fmtMonth} />
          <YAxis
            stroke={AXIS_STROKE}
            tick={AXIS_TICK}
            tickFormatter={(v) => `${Math.round(v * 100)}%`}
          />
          <Tooltip
            {...TOOLTIP_STYLE}
            labelFormatter={fmtMonth}
            formatter={(v, name) => [`${typeof v === "number" ? v.toFixed(1) : v}%`, ORIENT_NAMES[name] || name]}
          />
          <Legend wrapperStyle={CHART.legend} formatter={(v) => ORIENT_NAMES[v] || v} />
          {[...data.orientations].reverse().map((o) => (
            <Area
              key={o}
              type="monotone"
              dataKey={o}
              stackId="1"
              stroke={ORIENT_COLORS[o]}
              fill={ORIENT_COLORS[o]}
              fillOpacity={0.8}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
    {/* Momentum indicators */}
    <div className="mt-4 flex flex-wrap gap-3">
      {data.orientations.map((o) => {
        const m = data.momentum[o];
        const Icon = m.change_pct > 2 ? ArrowUpRight : m.change_pct < -2 ? ArrowDownRight : Minus;
        const colorClass = m.change_pct > 2 ? "text-emerald-400" : m.change_pct < -2 ? "text-red-400" : "text-muted-foreground";
        return (
          <div key={o} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/20 border border-border">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ORIENT_COLORS[o] }} />
            <span className="text-xs font-medium">{ORIENT_NAMES[o]}</span>
            <Icon className={`w-3.5 h-3.5 ${colorClass}`} />
            <span className={`text-xs font-semibold ${colorClass}`}>
              {m.change_pct > 0 ? "+" : ""}{m.change_pct}%
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

/* ────── 4. Consumption Patterns ────── */
const ConsumptionPatterns = ({ data }) => (
  <div className="space-y-6" data-testid="consumption-patterns">
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="mb-1">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-primary" />
          Monetization Evolution (Quarterly)
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          How business models have shifted over time. Rising share of freemium/subscription indicates market maturation.
        </p>
      </div>
      <div className="h-80 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.monetization_evolution} stackOffset="expand">
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis dataKey="quarter" stroke={AXIS_STROKE} tick={AXIS_TICK} interval={3} />
            <YAxis stroke={AXIS_STROKE} tick={AXIS_TICK} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
            <Tooltip
              {...TOOLTIP_STYLE}
              formatter={(v, name) => [`${typeof v === "number" ? v.toFixed(1) : v}%`, MONET_NAMES[name] || name]}
            />
            <Legend wrapperStyle={CHART.legend} formatter={(v) => MONET_NAMES[v] || v} />
            {data.monetization_models.map((mm) => (
              <Area
                key={mm}
                type="monotone"
                dataKey={mm}
                stackId="1"
                stroke={MONET_COLORS[mm] || "#6b7280"}
                fill={MONET_COLORS[mm] || "#6b7280"}
                fillOpacity={0.75}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>

    {/* Regional Consumption Heatmap */}
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-base font-semibold flex items-center gap-2 mb-4">
        <ShoppingCart className="w-4 h-4 text-primary" />
        Regional Monetization Preferences
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="regional-monetization-table">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2 font-medium text-muted-foreground">Region</th>
              {data.monetization_models.map((mm) => (
                <th key={mm} className="text-center p-2 font-medium text-muted-foreground text-xs">
                  {MONET_NAMES[mm] || mm}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.regional_consumption.map((r) => (
              <tr key={r.region} className="border-b border-border/40 hover:bg-muted/20">
                <td className="p-2 font-medium text-xs whitespace-nowrap">{r.region}</td>
                {data.monetization_models.map((mm) => {
                  const val = r.breakdown[mm] || 0;
                  const intensity = Math.min(val / 60, 1);
                  return (
                    <td key={mm} className="text-center p-2">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-semibold tabular-nums"
                        style={{
                          backgroundColor: val > 0
                            ? `${MONET_COLORS[mm] || "#6b7280"}${Math.round(intensity * 40 + 15).toString(16).padStart(2, "0")}`
                            : "transparent",
                          color: val > 5 ? MONET_COLORS[mm] || "#6b7280" : "hsl(240, 5%, 45%)",
                        }}
                      >
                        {val > 0 ? `${val}%` : "—"}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

/* ────── 5. Predictions & Outlook ────── */
const PredictionsOutlook = ({ data }) => {
  const gr = data.growth_rates;
  const momentum = data.momentum;
  const partial = data.partial_year;

  const predictions = [];

  const latestGrowth = gr.length > 0 ? gr[gr.length - 1] : null;
  const prevGrowth = gr.length > 1 ? gr[gr.length - 2] : null;
  if (latestGrowth && prevGrowth) {
    const decelerating = latestGrowth.yoy_growth_pct < prevGrowth.yoy_growth_pct;
    predictions.push({
      title: decelerating ? "Growth Decelerating" : "Sustained Expansion",
      text: decelerating
        ? `Full-year YoY growth fell from ${prevGrowth.yoy_growth_pct}% (${prevGrowth.year}) to ${latestGrowth.yoy_growth_pct}% (${latestGrowth.year}). The market is transitioning from explosive growth to maturation. Expect consolidation.${partial ? ` (${partial.year}: ${partial.count.toLocaleString()} apps through ${partial.months_elapsed}mo, ~${partial.annualized_estimate.toLocaleString()} annualized.)` : ""}`
        : `YoY growth accelerated from ${prevGrowth.yoy_growth_pct}% to ${latestGrowth.yoy_growth_pct}%. Still in early adoption phase.`,
      signal: decelerating ? "caution" : "positive",
      metric: `${latestGrowth.yoy_growth_pct > 0 ? "+" : ""}${latestGrowth.yoy_growth_pct}%`,
      metricLabel: `${latestGrowth.year} YoY (full year)`,
    });
  }

  const utilMom = momentum.utility;
  predictions.push({
    title: "Utility AI Entrenchment",
    text: `Utility AI shows ${utilMom.change_pct > 0 ? "continued" : "slowing"} momentum (${utilMom.change_pct > 0 ? "+" : ""}${utilMom.change_pct}%). Enterprise productivity tools and AI copilots remain the dominant category.`,
    signal: "neutral",
    metric: `${utilMom.recent.toLocaleString()}`,
    metricLabel: "Recent 6-month launches",
  });

  const creativeMom = momentum.creative;
  predictions.push({
    title: creativeMom.change_pct > 5 ? "Creative AI Surge" : "Creative AI Plateau",
    text: creativeMom.change_pct > 5
      ? `Creative AI is accelerating (+${creativeMom.change_pct}%). Generative image, video, and music tools driving a new wave.`
      : `Creative AI momentum is stabilizing at ${creativeMom.change_pct > 0 ? "+" : ""}${creativeMom.change_pct}%. Market differentiating between commodity and specialized platforms.`,
    signal: creativeMom.change_pct > 5 ? "positive" : "neutral",
    metric: `${creativeMom.change_pct > 0 ? "+" : ""}${creativeMom.change_pct}%`,
    metricLabel: "6-month momentum",
  });

  const careMom = momentum.care;
  predictions.push({
    title: careMom.change_pct > 0 ? "Care AI: Steady Growth" : "Care AI: Growth Stalling",
    text: careMom.change_pct > 0
      ? `Care AI continues positive momentum (+${careMom.change_pct}%), driven by healthtech and edtech. Regulatory tailwinds may accelerate.`
      : `Care AI shows declining momentum (${careMom.change_pct}%). Relative share compressed by utility and creative categories.`,
    signal: careMom.change_pct > 0 ? "positive" : "caution",
    metric: `${careMom.change_pct > 0 ? "+" : ""}${careMom.change_pct}%`,
    metricLabel: "6-month momentum",
  });

  const extMom = momentum.extraction;
  predictions.push({
    title: extMom.change_pct > 10 ? "Extraction AI: Rising Concern" : "Extraction AI: Contained",
    text: extMom.change_pct > 10
      ? `Extraction AI growing at +${extMom.change_pct}%, the fastest among all categories. Signals increasing data-harvesting patterns. Regulatory scrutiny likely.`
      : `Extraction AI at ${(extMom.recent / (Object.values(momentum).reduce((s, v) => s + v.recent, 0)) * 100).toFixed(1)}% of recent launches. Not the dominant paradigm.`,
    signal: extMom.change_pct > 10 ? "caution" : "positive",
    metric: `${extMom.change_pct > 0 ? "+" : ""}${extMom.change_pct}%`,
    metricLabel: "6-month momentum",
  });

  const signalColors = {
    positive: "border-emerald-500/30 bg-emerald-500/5",
    caution: "border-amber-500/30 bg-amber-500/5",
    neutral: "border-blue-500/30 bg-blue-500/5",
  };
  const signalText = {
    positive: "text-emerald-400",
    caution: "text-amber-400",
    neutral: "text-blue-400",
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="predictions-outlook">
      <div className="mb-5">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          Predictions & Outlook
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Data-driven projections based on trend analysis of {data.total_apps.toLocaleString()} apps.
          These are observational inferences, not forecasts.
        </p>
      </div>

      {/* YoY Growth Bar Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Year-over-Year Growth Rate (Full Years Only)</h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.growth_rates}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis dataKey="year" stroke={AXIS_STROKE} tick={AXIS_TICK} />
              <YAxis stroke={AXIS_STROKE} tick={AXIS_TICK} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                {...TOOLTIP_STYLE}
                formatter={(v, name) => {
                  if (name === "yoy_growth_pct") return [`${v}%`, "YoY Growth"];
                  return [v.toLocaleString(), "Apps"];
                }}
              />
              <Bar dataKey="yoy_growth_pct" name="YoY Growth" radius={[4, 4, 0, 0]}>
                {data.growth_rates.map((entry, idx) => (
                  <Cell
                    key={`growth-cell-${entry.industry || idx}`}
                    fill={entry.yoy_growth_pct > 100 ? "#f59e0b" : entry.yoy_growth_pct > 20 ? "#10b981" : entry.yoy_growth_pct > 0 ? "#3b82f6" : "#ef4444"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {partial && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-violet-500/5 border border-violet-500/15">
            <p className="text-xs text-muted-foreground">
              <span className="text-violet-400 font-semibold">{partial.year} partial data:</span>{" "}
              {partial.count.toLocaleString()} apps in {partial.months_elapsed} months (annualized: ~{partial.annualized_estimate.toLocaleString()}).
              Excluded from YoY comparison to avoid misleading metrics.
            </p>
          </div>
        )}
      </div>

      {/* Prediction Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {predictions.map((p, i) => (
          <div
            key={p.title || i}
            className={`rounded-xl border p-4 ${signalColors[p.signal]}`}
            data-testid={`prediction-card-${i}`}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-semibold">{p.title}</h4>
              <div className="text-right flex-shrink-0 ml-3">
                <div className={`text-lg font-bold tabular-nums ${signalText[p.signal]}`}>{p.metric}</div>
                <div className="text-[10px] text-muted-foreground">{p.metricLabel}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{p.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 px-3 py-2 rounded-lg bg-muted/20 border border-border">
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          These predictions are derived from observable patterns in the dataset and do not constitute investment advice or market forecasts.
          The dataset reflects publicly listed AI applications and may not capture proprietary enterprise tools, internal models, or applications in stealth mode.
        </p>
      </div>
    </div>
  );
};

export default IndustryTrends;
